import React, { useEffect, useState } from "react";
import { Loader2, ScrollText, Filter, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";

const ENTITY_TYPES = [
    ["", "All entity types"],
    ["organization", "Organizations"],
    ["user", "Users"],
    ["invitation", "Invitations"],
    ["assessment_cycle", "Cycles"],
    ["domain_assessment", "Domain assessments"],
    ["look_for_rating", "Ratings"],
    ["evidence_file", "Evidence"],
    ["key_finding", "Findings"],
    ["red_flag_selected", "Red flags"],
    ["recommendation", "Recommendations"],
];

const ACTOR_ROLE_COLOR = {
    platform_admin: "bg-slate-100 text-slate-700 border-slate-200",
    org_leader: "bg-blue-50 text-blue-800 border-blue-200",
    reviewer: "bg-teal-50 text-teal-800 border-teal-200",
    coach: "bg-purple-50 text-purple-800 border-purple-200",
    teacher: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function AuditLogPage() {
    const { user } = useAuth();
    const isPlatform = user?.role === "platform_admin";
    const [orgs, setOrgs] = useState([]);
    const [orgId, setOrgId] = useState(isPlatform ? "" : user?.org_id || "");
    const [entityType, setEntityType] = useState("");
    const [rows, setRows] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isPlatform) return;
        api.get("/admin/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    }, [isPlatform]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 50 };
            if (orgId) params.org_id = orgId;
            if (entityType) params.entity_type = entityType;
            const { data } = await api.get("/admin/audit-log", { params });
            setRows(data.items || []);
            setNextCursor(data.next_cursor || null);
            setError(null);
        } catch (e) {
            setError(extractError(e, "Could not load audit log"));
        } finally {
            setLoading(false);
        }
    };

    const loadMore = async () => {
        if (!nextCursor) return;
        setLoadingMore(true);
        try {
            const params = { limit: 50, cursor: nextCursor };
            if (orgId) params.org_id = orgId;
            if (entityType) params.entity_type = entityType;
            const { data } = await api.get("/admin/audit-log", { params });
            setRows((prev) => [...prev, ...(data.items || [])]);
            setNextCursor(data.next_cursor || null);
        } catch (e) {
            toast.error(extractError(e, "Could not load more"));
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, entityType]);

    return (
        <div className="space-y-6" data-testid="audit-log-page">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                    Compliance & audit
                </div>
                <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight">
                    Audit log
                </h1>
                <p className="mt-2 text-sm text-[#0E1B2C]/70 max-w-2xl">
                    Every create, update and delete on this platform is recorded with actor, time,
                    entity, and IP. Read-only.
                </p>
            </div>

            <div className="rounded-2xl bg-white border border-[#D6E6F2] p-4 flex flex-wrap items-end gap-4" data-testid="audit-filters">
                {isPlatform && (
                    <div className="space-y-2 min-w-[220px]">
                        <Label>Organization</Label>
                        <Select value={orgId || "__all__"} onValueChange={(v) => setOrgId(v === "__all__" ? "" : v)}>
                            <SelectTrigger data-testid="audit-org-filter">
                                <SelectValue placeholder="All organizations" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All organizations</SelectItem>
                                {orgs.map((o) => (
                                    <SelectItem key={o.id} value={o.id}>
                                        {o.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                <div className="space-y-2 min-w-[220px]">
                    <Label>Entity type</Label>
                    <Select value={entityType || "__all__"} onValueChange={(v) => setEntityType(v === "__all__" ? "" : v)}>
                        <SelectTrigger data-testid="audit-entity-filter">
                            <SelectValue placeholder="All entity types" />
                        </SelectTrigger>
                        <SelectContent>
                            {ENTITY_TYPES.map(([v, l]) => (
                                <SelectItem key={v || "__all__"} value={v || "__all__"}>
                                    {l}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="text-[#0B2545] flex items-center gap-3">
                    <Loader2 className="animate-spin" size={18} /> Loading…
                </div>
            ) : error ? (
                <div className="text-red-700">{error}</div>
            ) : rows.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#8AC4FF] bg-white p-10 text-center">
                    <ScrollText size={24} className="mx-auto text-[#1E56A0]" />
                    <p className="mt-2 text-[#0B2545] font-semibold">No audit entries.</p>
                </div>
            ) : (
                <div className="rounded-2xl bg-white border border-[#D6E6F2] overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="audit-table">
                            <thead>
                                <tr className="text-left text-xs uppercase tracking-wider text-[#0E1B2C]/60 bg-[#F1F6FB]">
                                    <th className="px-4 py-3">When</th>
                                    <th className="px-4 py-3">Actor</th>
                                    <th className="px-4 py-3">Action</th>
                                    <th className="px-4 py-3">Entity</th>
                                    <th className="px-4 py-3">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const roleClass = ACTOR_ROLE_COLOR[r.actor_role] || "bg-slate-100 text-slate-700 border-slate-200";
                                    return (
                                        <tr key={r.id} className="border-t border-[#D6E6F2]" data-testid={`audit-row-${r.id}`}>
                                            <td className="px-4 py-2 whitespace-nowrap text-[#0E1B2C]/80">
                                                {new Date(r.occurred_at).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="font-medium text-[#0B2545]">{r.actor_email || "system"}</div>
                                                {r.actor_role && (
                                                    <Badge className={`${roleClass} border`} variant="outline">
                                                        {r.actor_role.replace(/_/g, " ")}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap font-mono text-xs">
                                                {r.action}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-xs">
                                                <div className="text-[#0B2545]">{r.entity_type}</div>
                                                <div className="text-[#0E1B2C]/60 font-mono">{(r.entity_id || "").slice(0, 8)}…</div>
                                            </td>
                                            <td className="px-4 py-2 text-xs text-[#0E1B2C]/70 font-mono max-w-[380px] truncate">
                                                {Object.keys(r.metadata || {}).length ? JSON.stringify(r.metadata) : "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {nextCursor && (
                        <div className="p-4 border-t border-[#D6E6F2] flex justify-center">
                            <button
                                onClick={loadMore}
                                disabled={loadingMore}
                                className="rounded-full border border-[#0B2545] text-[#0B2545] px-5 py-2 text-sm font-semibold hover:bg-[#0B2545] hover:text-white transition-colors"
                                data-testid="audit-load-more-btn"
                            >
                                {loadingMore ? "Loading…" : "Load more"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
